/**
 * Profile.jsx — iter167
 *
 * Self-service settings: change account email + password.
 *
 * Critical for the locked-out admin scenario: a user whose seed email isn't
 * a real inbox (e.g. admin@stockaris.com) can rotate to a real address here,
 * after which the forgot-password flow works normally.
 */
import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Envelope, Key, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changeMyEmail, changeMyPassword } from '../lib/api';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });
  const [emailBusy, setEmailBusy] = useState(false);

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email.trim()) return toast.error('Enter a new email');
    if (!emailForm.current_password) return toast.error('Confirm with your current password');
    setEmailBusy(true);
    try {
      const r = await changeMyEmail({
        new_email: emailForm.new_email.trim(),
        current_password: emailForm.current_password,
      });
      toast.success(r.data.message || 'Email updated');
      // Session is invalidated server-side — sign out + redirect to login.
      try { await logout(); } catch { /* noop */ }
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change email');
    } finally {
      setEmailBusy(false);
    }
  };

  const onPwSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 8) return toast.error('Password must be at least 8 characters');
    if (pwForm.new_password !== pwForm.confirm) return toast.error('Passwords do not match');
    setPwBusy(true);
    try {
      const r = await changeMyPassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success(r.data.message || 'Password updated');
      try { await logout(); } catch { /* noop */ }
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl space-y-8" data-testid="profile-page">
        <div>
          <p className="overline">Account</p>
          <h1 className="text-2xl font-bold">Profile &amp; security</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Signed in as <span className="font-mono">{user?.email}</span> · role{' '}
            <span className="font-mono">{user?.role}</span>
          </p>
        </div>

        {/* --- Change email ----------------------------------------------- */}
        <section className="border border-border bg-card p-5 space-y-3" data-testid="change-email-card">
          <div className="flex items-center gap-2">
            <Envelope size={18} weight="bold" />
            <h2 className="text-base font-bold">Change account email</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Use this if your account email isn&apos;t a real inbox (e.g. the seed default
            <span className="font-mono"> admin@stockaris.com</span>). After updating, the
            forgot-password flow can email you a reset link.
          </p>
          <div className="bg-amber-50/60 border border-amber-200 text-[11px] text-amber-900 px-3 py-2 flex gap-2 items-start">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
            <span>You&apos;ll be signed out of every device after changing your email.
              Sign in again with the new address.</span>
          </div>
          <form onSubmit={onEmailSubmit} className="space-y-3">
            <div>
              <Label>New email</Label>
              <Input
                type="email"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                placeholder="you@yourcompany.com"
                className="mt-1"
                data-testid="profile-new-email"
                autoComplete="email"
              />
            </div>
            <div>
              <Label>Current password</Label>
              <Input
                type="password"
                value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                placeholder="Confirm with your password"
                className="mt-1"
                data-testid="profile-email-current-pw"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={emailBusy}
              className="bg-brand hover:bg-brand/90"
              data-testid="profile-change-email-btn"
            >
              {emailBusy ? 'Updating…' : 'Change email'}
            </Button>
          </form>
        </section>

        {/* --- Change password -------------------------------------------- */}
        <section className="border border-border bg-card p-5 space-y-3" data-testid="change-password-card">
          <div className="flex items-center gap-2">
            <Key size={18} weight="bold" />
            <h2 className="text-base font-bold">Change password</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Minimum 8 characters with at least one letter and one digit.
          </p>
          <form onSubmit={onPwSubmit} className="space-y-3">
            <div>
              <Label>Current password</Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1"
                data-testid="profile-pw-current"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="mt-1"
                data-testid="profile-pw-new"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="mt-1"
                data-testid="profile-pw-confirm"
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              disabled={pwBusy}
              className="bg-brand hover:bg-brand/90"
              data-testid="profile-change-pw-btn"
            >
              {pwBusy ? 'Updating…' : 'Change password'}
            </Button>
          </form>
        </section>
      </div>
    </Layout>
  );
}
